"use client"

import Image from "next/image"
import { CheckCircle, X } from "lucide-react"

import { cn } from "@/lib/utils"

interface SamplePhotosProps {
  className?: string
  mode?: "identify" | "diagnose"
  /**
   * Where the good/bad label sits. "overlay" paints it over the photo's lower
   * edge (homepage). "below" sets it under the frame so the photo stays
   * unobstructed; the exit-scan modal uses this because at 375px the overlay
   * text collided with the grass texture.
   */
  captionPlacement?: "overlay" | "below"
}

// Pre-generated 10x10 blurred JPEG previews of /perfect.JPEG and /avoid.JPEG
// (via sharp). Used as next/image blur placeholders so the photos appear
// pre-filled with a low-fi version of themselves instead of flashing in from
// an empty / solid-color background.
const PERFECT_BLUR =
  "data:image/jpeg;base64,/9j/2wBDABALDA4MChAODQ4SERATGCgaGBYWGDEjJR0oOjM9PDkzODdASFxOQERXRTc4UG1RV19iZ2hnPk1xeXBkeFxlZ2P/2wBDARESEhgVGC8aGi9jQjhCY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2P/wAARCAAKAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAgT/xAAbEAACAgMBAAAAAAAAAAAAAAAAAQQRAhIhQv/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAFhEBAQEAAAAAAAAAAAAAAAAAAAES/9oADAMBAAIRAxEAPwCbOY9uMalOiPwwWTMyDf/Z"

const AVOID_BLUR =
  "data:image/jpeg;base64,/9j/2wBDABALDA4MChAODQ4SERATGCgaGBYWGDEjJR0oOjM9PDkzODdASFxOQERXRTc4UG1RV19iZ2hnPk1xeXBkeFxlZ2P/2wBDARESEhgVGC8aGi9jQjhCY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2P/wAARCAAKAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAQT/xAAcEAACAAcAAAAAAAAAAAAAAAAAAQIDBBMxQVH/xAAVAQEBAAAAAAAAAAAAAAAAAAADBP/EABYRAQEBAAAAAAAAAAAAAAAAAAABAv/aAAwDAQACEQMRAD8AldXG0m8hem9J9iQTMG//2Q=="

// Diagnose reference shots. Sourced from the Claude Design prototype, where
// they were AI-generated (gpt-image; C2PA digitalSourceType
// trainedAlgorithmicMedia) rather than photographed. That provenance did not
// survive the re-encode from 3MB PNG to web-weight JPEG, and the tiles carry no
// visible label, so this comment is the only remaining record — keep it with
// the files. They illustrate framing only; do not reuse them anywhere that
// implies a real customer lawn or a real diagnosis result.
const DIAGNOSE_EDGE_BLUR =
  "data:image/jpeg;base64,/9j/2wBDABQODxIPDRQSEBIXFRQYHjIhHhwcHj0sLiQySUBMS0dARkVQWnNiUFVtVkVGZIhlbXd7gYKBTmCNl4x9lnN+gXz/2wBDARUXFx4aHjshITt8U0ZTfHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHz/wAARCAAKAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAwL/xAAfEAABAwMFAAAAAAAAAAAAAAABAAIRAwQxEiEycaH/xAAVAQEBAAAAAAAAAAAAAAAAAAABBP/EABkRAAEFAAAAAAAAAAAAAAAAAAABESExUf/aAAwDAQACEQMRAD8AFtUxqg4zCsXL45O8Q1NmjoIFK2CsUf/Z"

const DIAGNOSE_CONTEXT_BLUR =
  "data:image/jpeg;base64,/9j/2wBDABQODxIPDRQSEBIXFRQYHjIhHhwcHj0sLiQySUBMS0dARkVQWnNiUFVtVkVGZIhlbXd7gYKBTmCNl4x9lnN+gXz/2wBDARUXFx4aHjshITt8U0ZTfHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHz/wAARCAAKAAoDASIAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAABAID/8QAHRAAAQQCAwAAAAAAAAAAAAAAAQACAxEUUUFhkv/EABUBAQEAAAAAAAAAAAAAAAAAAAEE/8QAFREBAQAAAAAAAAAAAAAAAAAAABH/2gAMAwEAAhEDEQA/ABidzrp5HSrIk23yEUcrKztSQV//2Q=="

const DIAGNOSE_SHOTS = [
  {
    src: "/examples/lawn-damage-closeup.jpg",
    blur: DIAGNOSE_EDGE_BLUR,
    alt: "Close-up of the boundary where healthy green turf meets a straw-colored damaged patch",
    label: "1. Problem edge",
    caption: "Show healthy and damaged grass together, in focus.",
  },
  {
    src: "/examples/lawn-damage-context.jpg",
    blur: DIAGNOSE_CONTEXT_BLUR,
    alt: "Wider view of a rounded dead patch surrounded by healthy lawn, with more patches behind it",
    label: "2. Wider context",
    caption: "Include the full patch, its shape, and nearby turf.",
  },
] as const

export function SamplePhotos({ className, mode = "identify", captionPlacement = "overlay" }: SamplePhotosProps) {
  if (mode === "diagnose") {
    return (
      <div
        className={cn(
          "mx-auto mb-3 grid max-w-[18rem] grid-cols-2 gap-3 sm:mb-5 sm:max-w-xl sm:gap-4 md:gap-6",
          className,
        )}
        aria-label="Photos needed for lawn diagnosis"
      >
        {DIAGNOSE_SHOTS.map((shot) => (
          <div key={shot.src} className="text-left">
            <div className="relative overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
              <Image
                src={shot.src}
                alt={shot.alt}
                width={800}
                height={800}
                className="block aspect-square w-full object-cover"
                placeholder="blur"
                blurDataURL={shot.blur}
                sizes="(max-width: 768px) 45vw, 200px"
              />
            </div>
            <p className="mt-1.5 text-xs font-bold uppercase tracking-wide text-gray-900">{shot.label}</p>
            <p className="mt-0.5 text-xs leading-snug text-gray-600">{shot.caption}</p>
          </div>
        ))}
      </div>
    )
  }

  const below = captionPlacement === "below"

  return (
    <div className={cn("mx-auto mb-3 grid max-w-[18rem] grid-cols-2 gap-3 sm:mb-5 sm:max-w-xl sm:gap-4 md:gap-6", className)}>
      {/* Good Example */}
      <div className="text-center">
        <div className="group relative overflow-hidden rounded-2xl shadow-md transition-transform duration-200 hover:-translate-y-0.5">
          <Image
            src="/perfect.JPEG"
            alt="Good example - close-up grass photo"
            width={320}
            height={240}
            className="w-full aspect-4/3 object-cover"
            priority
            placeholder="blur"
            blurDataURL={PERFECT_BLUR}
            sizes="(max-width: 768px) 50vw, 320px"
          />
          {!below && (
            <div className="absolute inset-0 bg-linear-to-b from-transparent from-50% to-black/55" aria-hidden="true" />
          )}
          <div className="absolute top-2.5 right-2.5 w-[26px] h-[26px] bg-green-600 rounded-full flex items-center justify-center text-white">
            <CheckCircle className="w-4 h-4" />
          </div>
          {!below && (
            <div className="absolute left-3 bottom-2.5">
              <span className="text-white text-[13px] font-semibold">Close-up, good lighting</span>
            </div>
          )}
        </div>
        {below && <p className="mt-1.5 text-xs font-semibold text-green-700">Close-up, good light</p>}
      </div>

      {/* Bad Example */}
      <div className="text-center">
        <div className="group relative overflow-hidden rounded-2xl shadow-md transition-transform duration-200 hover:-translate-y-0.5">
          <Image
            src="/avoid.JPEG"
            alt="Bad example - too far away"
            width={320}
            height={240}
            className="w-full aspect-4/3 object-cover"
            placeholder="blur"
            blurDataURL={AVOID_BLUR}
            sizes="(max-width: 768px) 50vw, 320px"
          />
          {!below && (
            <div className="absolute inset-0 bg-linear-to-b from-transparent from-50% to-black/55" aria-hidden="true" />
          )}
          <div className="absolute top-2.5 right-2.5 w-[26px] h-[26px] bg-red-600 rounded-full flex items-center justify-center text-white">
            <X className="w-4 h-4" />
          </div>
          {!below && (
            <div className="absolute left-3 bottom-2.5">
              <span className="text-white text-[13px] font-semibold">Too far, blurry</span>
            </div>
          )}
        </div>
        {below && <p className="mt-1.5 text-xs font-semibold text-gray-500">Too far, blurry</p>}
      </div>
    </div>
  )
}
